/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { AppSkeletonImage } from '@jira/platform__skeleton-image-container/src/ui/AppSkeletonImage.tsx';
import { token } from '@atlaskit/tokens';

import { CompactModeProvider } from '@atlassian/jira-issue-view-compact-mode/src/services/provider/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';

import { expVal } from '@atlassian/jira-feature-experiments';
import imgSVG from './assets/issue-app-skeleton.tpl.svg';
import imgSVGExp from './assets/issue-app-skeleton.exp.tpl.svg';

const fullpageStyles = css({
	borderTopWidth: token('space.300'),
});
const modalStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '-40px',
});
const customSpacingStyles = css({
	maxWidth: '1920px',
	marginTop: token('space.0'),
	marginRight: 'auto',
	marginBottom: token('space.0'),
	marginLeft: 'auto',
	borderLeftWidth: token('space.400'),
	borderRightWidth: token('space.400'),
	/* Fix FF 2x scale bug on high res devices https://bugzilla.mozilla.org/show_bug.cgi?id=986403 */
	'@media (min--moz-device-pixel-ratio: 2)': {
		maxWidth: '840px',
		borderLeftWidth: token('space.200'),
		borderRightWidth: token('space.200'),
	},
});

export type IssueViewSkeletonProps = {
	isEmbedView?: boolean;
	isModalView?: boolean;
};
/**
 * Renders a placeholder for the Issue View that adapts its layout and styling to different contexts such as embedding or modal presentation, ensuring a consistent user experience during content loading.
 */
const IssueViewSkeletonWithRightStatus = ({ isEmbedView, isModalView }: IssueViewSkeletonProps) => {
	// same as getIssueContainerMaxWidth
	const customCss = [
		customSpacingStyles,
		isEmbedView !== true && isModalView !== true ? fullpageStyles : null,
		isModalView === true ? modalStyles : null,
	];
	return (
		<CompactModeProvider>
			<AppSkeletonImage src={imgSVG} css={customCss} />
		</CompactModeProvider>
	);
};

const IssueViewSkeletonWithLeftStatus = ({ isEmbedView, isModalView }: IssueViewSkeletonProps) => {
	// same as getIssueContainerMaxWidth
	const customCss = [
		customSpacingStyles,
		isEmbedView !== true && isModalView !== true ? fullpageStyles : null,
		isModalView === true ? modalStyles : null,
	];
	return (
		<CompactModeProvider>
			<AppSkeletonImage src={imgSVGExp} css={customCss} />
		</CompactModeProvider>
	);
};

export const IssueViewSkeleton = componentWithCondition(
	() => expVal('move_status_to_summary_ga', 'isEnabled', false),
	IssueViewSkeletonWithLeftStatus,
	IssueViewSkeletonWithRightStatus,
);
